import { useState } from 'react';
import { ArrowRightLeft, Clock, Undo2, Upload } from 'lucide-react';
import { assertNever, FINANCE_IMPORT_SOURCES, importSourceLabel } from '@signalvote/shared';
import type { BankAccountDto, ImportBatchHistoryItem, ImportBatchStatus } from '@signalvote/shared';
import { useBankAccounts } from '@/hooks/queries/useBankIngestion';
import { useImportBatchHistory } from '@/hooks/queries/useFinanceImportBatches';
import { useDialogState } from '@/hooks';
import { formatDateTime } from '@/components/finance/utils';
import { useCampaignStore } from '@/stores';
import { cn } from '@/lib/utils';
import { InlineSpinner } from '@/components/ui/inline-spinner';
import { MoveImportDialog } from './MoveImportDialog';
import { UndoImportDialog } from './UndoImportDialog';

/**
 * ADR-015 D11 — the real import-history surface on the reframed Sources front
 * door. Replaces the deleted fake "Connected" cards — static mock teasers, not
 * live integrations — with the actual list of import batches from
 * `UndoImportService.ListImportHistoryAsync`, leaning on real data
 * (`feedback_no_transient_persistence`) — no mock, no parallel fetch. (Real bank
 * auto-sync now exists via ADR-070 Plaid bank-pull; it's set up on the Banks &
 * processors page and its synced rows flow through this same triage/import
 * pipeline, not a card here.)
 *
 * Treasurer-first throughout: the engineer-facing `ImportBatch.Source` string is
 * translated at the render boundary (`importSourceLabel`), and the three import
 * statuses read as plain words. Active is the only live row; Undone and
 * Superseded are both non-live and render struck-through.
 *
 * The empty state always shows (with a first-upload nudge) so the region is a
 * stable home for the multi-file CTA above it — never an integration teaser.
 */
interface SourcesImportHistoryProps {
  /** Opens the multi-file upload dialog from the empty-state CTA. */
  onUpload?: () => void;
}

/**
 * Treasurer-first label for an import batch's lifecycle status. Exhaustive over
 * the three wire values — a future 4th `ImportBatchStatus` fails the build here
 * rather than rendering a raw enum string into the history list.
 */
function statusLabel(status: ImportBatchStatus): string {
  switch (status) {
    case 'Active':
      return 'Imported';
    case 'Undone':
      return 'Undone';
    case 'Superseded':
      return 'Replaced by a re-import';
    default:
      return assertNever(status);
  }
}

// Active is the only live import; Undone and Superseded are both historical and
// strike through (the row stays in the list — it's part of the audit trail).
function isHistorical(status: ImportBatchStatus): boolean {
  return status !== 'Active';
}

// The persisted `ImportBatch.Source` for a file the detector couldn't classify
// (the "Not sure — figure it out later" / "Other processor" path). The backend
// preserves the uploaded bytes as a 0-row batch — it imported NOTHING — so this
// must never present as "Imported". The same string keys `importSourceLabel`.
const UNATTRIBUTED_SOURCE = FINANCE_IMPORT_SOURCES.Unattributed;

// An unclassified file preserved its bytes but produced zero rows. It's honest
// to call this "Needs classification", not "Imported" (ADR-015 / B3) — derived
// purely from the existing Source signal already on the history DTO.
function isUnclassified(batch: ImportBatchHistoryItem): boolean {
  return batch.status === 'Active' && batch.source === UNATTRIBUTED_SOURCE;
}

export function SourcesImportHistory({ onUpload }: SourcesImportHistoryProps) {
  const { data: batches, isLoading } = useImportBatchHistory();
  const { data: accounts = [] } = useBankAccounts();

  return (
    <section aria-label="Import history" className="space-y-3">
      <h2 className="text-sm font-semibold text-foreground">Recent imports</h2>

      {isLoading && (
        <div className="flex items-center gap-2 rounded-lg border border-border px-4 py-6 text-sm text-muted-foreground">
          <InlineSpinner decorative />
          Loading your import history…
        </div>
      )}

      {!isLoading && (!batches || batches.length === 0) && (
        <EmptyState onUpload={onUpload} />
      )}

      {!isLoading && batches && batches.length > 0 && (
        <ul className="space-y-2">
          {batches.map((batch) => (
            <ImportBatchRow key={batch.importBatchId} batch={batch} accounts={accounts} onUpload={onUpload} />
          ))}
        </ul>
      )}
    </section>
  );
}

function EmptyState({ onUpload }: { onUpload?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border px-4 py-10 text-center">
      <div className="grid h-11 w-11 place-items-center rounded-lg bg-muted text-muted-foreground">
        <Upload className="h-5 w-5" aria-hidden />
      </div>
      <button
        type="button"
        onClick={onUpload}
        disabled={!onUpload}
        className="min-h-11 rounded-md px-3 py-1.5 text-base font-semibold text-foreground hover:bg-muted disabled:cursor-default disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        Upload your first file
      </button>
      <p className="max-w-md text-sm text-muted-foreground">
        We accept bank statements, processor exports, or your own donation or
        expense sheets.
      </p>
    </div>
  );
}

function ImportBatchRow({
  batch,
  accounts,
  onUpload,
}: {
  batch: ImportBatchHistoryItem;
  /** The campaign's bank accounts; a bank-file import can move to any other one. */
  accounts: BankAccountDto[];
  /** Reopens the upload flow — the "Classify this file" action routes here. */
  onUpload?: () => void;
}) {
  const historical = isHistorical(batch.status);
  const unclassified = isUnclassified(batch);
  // Undo is a Manage-gated mutation. Match the capability pattern used by
  // DownloadAuditPacketButton: a UX gate only — the server's
  // [RequireCapability(Finance, Manage)] on the undo endpoint is the real
  // boundary. The control shows only on Active ("Imported") rows; Undone /
  // Superseded rows are part of the audit trail and can't be undone again.
  const canManage = useCampaignStore((s) => s.canManage('Finance'));
  const canUndo = canManage && batch.status === 'Active';
  const [confirmOpen, setConfirmOpen] = useState(false);
  const moveDialog = useDialogState();
  const currentAccountId = batch.bankAccountId;
  const canMove = canUndo && currentAccountId != null && accounts.some((a) => a.id !== currentAccountId);
  // Counts are surfaced only when present so a batch whose rows aren't counted
  // in this list shape (e.g. an off-platform expenses batch — expenditures are
  // not yet in the history count payload) never reads a misleading "0".
  const counts: string[] = [];
  if (batch.contributionCount > 0) {
    counts.push(`${batch.contributionCount} ${batch.contributionCount === 1 ? 'row' : 'rows'}`);
  }
  if (batch.bankTransactionCount > 0) {
    counts.push(
      `${batch.bankTransactionCount} bank ${batch.bankTransactionCount === 1 ? 'line' : 'lines'}`,
    );
  }

  return (
    <li className="rounded-lg border border-border px-4 py-3">
      {/* Wraps (no fixed widths / no truncate) so it stays legible at font-scale
          1.3 / 200% zoom, stacking to a single column on narrow widths. */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span
            className={cn(
              'text-sm font-semibold text-foreground',
              historical && 'line-through text-muted-foreground',
            )}
          >
            {batch.originalFileName}
          </span>
          <span className="text-xs text-muted-foreground">
            {importSourceLabel(batch.source)}
          </span>
          {batch.bankAccountName && (
            <span className="text-xs text-muted-foreground">{batch.bankAccountName}</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {counts.length > 0 && <span>{counts.join(' · ')}</span>}
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" aria-hidden />
            {formatDateTime(batch.uploadedAt)}
          </span>
          <StatusBadge status={batch.status} unclassified={unclassified} />
          {/* B3 — an unclassified preserved file imported nothing. Offer a way
              to classify it. No in-place reprocess endpoint exists yet (the
              preserved bytes can only be re-run by re-selecting the file from
              disk), so this reopens the uploader rather than inventing a backend
              path. The label says so plainly — "Classify this file" would
              over-promise a one-click reclassify this row can't deliver. An
              in-place reclassify is a backend follow-up. */}
          {unclassified && (
            <button
              type="button"
              onClick={onUpload}
              disabled={!onUpload}
              className="inline-flex min-h-9 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-foreground hover:bg-muted disabled:cursor-default disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Upload it again to classify
            </button>
          )}
          {canUndo && (
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              className="inline-flex min-h-9 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Undo2 className="h-3.5 w-3.5" aria-hidden />
              Undo
            </button>
          )}
          {canMove && (
            <button
              type="button"
              onClick={() => moveDialog.open()}
              className="inline-flex min-h-9 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <ArrowRightLeft className="h-3.5 w-3.5" aria-hidden />
              Move to another account
            </button>
          )}
        </div>
      </div>

      {canUndo && (
        <UndoImportDialog
          batchId={batch.importBatchId}
          originalFileName={batch.originalFileName}
          concurrencyVersion={batch.concurrencyVersion}
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          // The undo mutation already invalidates importBatches.all on success,
          // so the history list refetches and this row flips to "Undone" — we
          // just close the modal.
          onUndone={() => setConfirmOpen(false)}
        />
      )}

      {canMove && (
        <MoveImportDialog
          batchId={batch.importBatchId}
          originalFileName={batch.originalFileName}
          currentAccountId={currentAccountId}
          concurrencyVersion={batch.concurrencyVersion}
          accounts={accounts}
          open={moveDialog.isOpen}
          onOpenChange={(next) => (next ? moveDialog.open() : moveDialog.close())}
        />
      )}
    </li>
  );
}

function StatusBadge({
  status,
  unclassified,
}: {
  status: ImportBatchStatus;
  /**
   * B3 — an Active batch whose Source is Unattributed imported nothing (bytes
   * preserved). It reads "Needs classification" on an amber surface, never the
   * green "Imported" — the green badge would claim an import that didn't happen.
   */
  unclassified?: boolean;
}) {
  const historical = isHistorical(status);
  if (unclassified) {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
        Needs classification
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        historical ? 'bg-muted text-muted-foreground' : 'bg-emerald-50 text-emerald-700',
      )}
    >
      {statusLabel(status)}
    </span>
  );
}
